import { DocumentTypes } from '@introspection/documentTypes';
import { IBoardColumnInputType, IBoardInputType, IQuery } from '@introspection/index';
import { Injectable } from '@rxdi/core';
import { DataProxy, deepCopy, importQuery } from '@rxdi/graphql-client';
import { DocumentNode } from 'graphql';
import { map } from 'rxjs/operators';

import { BaseGraphqlService } from '../base';
import { addColumnToState, removeColumnFromState, updateColumnToState } from './board.helpers';

@Injectable()
export class BoardService extends BaseGraphqlService {
  createBoard({ projectId, payload }: { payload: IBoardInputType; projectId: string }) {
    return this.mutate({
      mutation: 'create-board.graphql',
      variables: {
        projectId,
        payload,
      },
      update: (proxy, { data: { createBoard } }) => {
        const data = this._getProjectState(proxy, projectId);
        data.getProject.boards.push(createBoard);
        this._writeProjectState(data)(proxy, projectId);
      },
    }).pipe(map((res) => res.data.createBoard));
  }

  updateBoard({ id, payload }: { payload: IBoardInputType; id: string }) {
    return this.mutate({
      mutation: 'update-board.graphql',
      variables: {
        id,
        payload,
      },
      update: (proxy, { data: { updateBoard } }) => {
        // const data = this._getProjectState(proxy, projectId);
        // data.getProject.boards.push(createBoard);
        // this._writeProjectState(data)(proxy, projectId);
      },
    }).pipe(map((res) => res.data.updateBoard));
  }

  getBoard(id: string) {
    return this.watchQuery({
      query: 'get-board.query.graphql',
      variables: {
        id,
      },
    }).pipe(map(({ data }) => data.getBoard));
  }

  boardChanged(boardId: string) {
    return this.subscribe({
      query: 'board-changed.graphql',
      variables: {
        boardId,
      },
    });
  }

  deleteBoard(projectId: string, id: string) {
    return this.mutate({
      mutation: 'delete-board.graphql',
      variables: {
        id,
      },
      update: (proxy) => {
        const data = this._getProjectState(proxy, projectId);
        data.getProject.boards = data.getProject.boards.filter((board) => board.id !== id);
        this._writeProjectState(data)(proxy, projectId);
      },
    }).pipe(map((res) => res.data.deleteBoard));
  }

  createColumn(boardId: string, projectId: string, payload: IBoardColumnInputType) {
    return this.mutate({
      mutation: 'create-board-column.graphql',
      variables: {
        boardId,
        payload,
      },
      update: (proxy, { data: { createBoardColumn } }) => {
        const state = this._getState(proxy, boardId);
        state.getBoard = addColumnToState()(createBoardColumn)(state.getBoard);
        this._writeState(state)(proxy, boardId);
      },
    }).pipe(map((res) => res.data.createBoardColumn));
  }

  updateColumn(id: string, boardId: string, payload: IBoardColumnInputType) {
    return this.mutate({
      mutation: 'update-board-column.graphql',
      variables: {
        id,
        payload,
      },
      update: (proxy, { data: { updateBoardColumn } }) => {
        const state = this._getState(proxy, boardId);
        state.getBoard = updateColumnToState()(updateBoardColumn)(state.getBoard);
        this._writeState(state)(proxy, boardId);
      },
    }).pipe(map((res) => res.data.updateBoardColumn));
  }

  deleteColumn(id: string) {
    return this.mutate({
      mutation: 'delete-board-column.graphql',
      variables: {
        id,
      },
      update: (proxy, { data: { deleteBoardColumn } }) => {
        const state = this._getState(proxy, deleteBoardColumn.boardId);
        state.getBoard = removeColumnFromState()(deleteBoardColumn)(state.getBoard);
        this._writeState(state)(proxy, deleteBoardColumn.boardId);
      },
    }).pipe(map((res) => res.data.deleteBoardColumn));
  }

  listProjectBacklog(projectId: string) {
    return this.query({
      query: 'list-boards-per-project.query.graphql',
      variables: { projectId },
    }).pipe(map((res) => res.data.listProjectBacklog));
  }

  private _getQuery(): DocumentNode {
    return importQuery<DocumentTypes>('get-board.query.graphql');
  }

  private _getState = (proxy: DataProxy, boardId: string) =>
    deepCopy(
      proxy.readQuery<IQuery>({
        query: this._getQuery(),
        variables: { id: boardId },
      })
    );

  private _writeState = (data: IQuery) => (proxy: DataProxy, boardId: string) =>
    proxy.writeQuery<IQuery>({
      query: this._getQuery(),
      variables: { id: boardId },
      data,
    });

  private _getProjectQuery(): DocumentNode {
    return importQuery<DocumentTypes>('get-project.query.graphql');
  }

  private _getProjectState = (proxy: DataProxy, projectId: string) =>
    deepCopy(
      proxy.readQuery<IQuery>({
        query: this._getProjectQuery(),
        variables: { id: projectId },
      })
    );

  private _writeProjectState = (data: IQuery) => (proxy: DataProxy, boardId: string) =>
    proxy.writeQuery<IQuery>({
      query: this._getProjectQuery(),
      variables: { id: boardId },
      data,
    });
}
